"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * §02.6 Sheet. "Sheets are mobile-only and slide from the bottom": full
 * width, radius-0, a 36×3 grabber, a title row with a 44px close target, a
 * body that scrolls independently, and an action bar pinned to the bottom
 * edge under a 1px hairline. Motion is motion-sheet: 260ms
 * cubic-bezier(0.2,0,0,1) on translateY, scrim 140ms; reduced motion drops
 * the transform and appears in place.
 *
 * `side` still accepts the three other edges for the two legacy drawers,
 * but the default is now `bottom` so a new consumer gets the spec'd sheet.
 *
 * Radius: §02.6's measurement row says "radius-0 · top edge only" while
 * §17.1 hands the bottom sheet radius-grab (16px, top two corners) as the
 * one grabbed surface. The launch brief rules radius 0; the conflict is
 * recorded in HANDOFF.md for the owner.
 */

const SHEET_MOTION = "motion-safe:data-[state=closed]:duration-[260ms] motion-safe:data-[state=open]:duration-[260ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)]"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      // Scrim: 72% canvas, fades at motion-state (140ms). Layer: the sheet
      // tier. `--ls-z-sheet` only resolves inside [data-ls] today, so the
      // fallback carries the site's current overlay tier until the z tokens
      // are hoisted (see HANDOFF.md).
      className={cn(
        "fixed inset-0 z-[var(--ls-z-sheet,50)] bg-[color:color-mix(in_srgb,var(--tls-canvas)_72%,transparent)] opacity-100 transition-opacity duration-[140ms] ease-[var(--motion-ease-emphasis)] starting:opacity-0 data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
        className
      )}
      {...props}
    />
  )
}

function SheetContent({
  className,
  children,
  side = "bottom",
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-[var(--ls-z-sheet,50)] flex flex-col gap-4 bg-[var(--tls-surface)] text-[var(--tls-fg-strong)] opacity-100 transition-[opacity,transform] duration-[140ms] ease-[var(--motion-ease-emphasis)] starting:opacity-0 data-[state=closed]:opacity-0 data-[state=open]:opacity-100",
          SHEET_MOTION,
          side === "right" &&
            "inset-y-0 right-0 h-full w-3/4 [transform:translateX(0)] border-l border-[color:var(--tls-border-subtle)] motion-safe:starting:[transform:translateX(100%)] motion-safe:data-[state=closed]:[transform:translateX(100%)] sm:max-w-sm",
          side === "left" &&
            "inset-y-0 left-0 h-full w-3/4 [transform:translateX(0)] border-r border-[color:var(--tls-border-subtle)] motion-safe:starting:[transform:translateX(-100%)] motion-safe:data-[state=closed]:[transform:translateX(-100%)] sm:max-w-sm",
          side === "top" &&
            "inset-x-0 top-0 h-auto [transform:translateY(0)] border-b border-[color:var(--tls-border-subtle)] motion-safe:starting:[transform:translateY(-100%)] motion-safe:data-[state=closed]:[transform:translateY(-100%)]",
          // §02.6: 100% wide, radius-0, top edge only; the body caps at a
          // viewport fraction so the pinned action bar is always on screen.
          // Dark swaps elev-1 for the 1px edge, which is what the border-top
          // is in both themes here (the system has no shadows on content).
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto max-h-[85dvh] w-full rounded-[var(--ls-radius-0)] [transform:translateY(0)] border-t border-[color:var(--tls-border-subtle)] pt-4 motion-safe:starting:[transform:translateY(100%)] motion-safe:data-[state=closed]:[transform:translateY(100%)]",
          className
        )}
        {...props}
      >
        {side === "bottom" && (
          // ① Grabber: 36×3, decorative. Drag-to-dismiss is not wired; the
          // close target and the scrim close the sheet.
          <span
            aria-hidden="true"
            data-slot="sheet-grabber"
            className="absolute top-2 left-1/2 w-9 -translate-x-1/2 rounded-[var(--ls-radius-0)] bg-[var(--tls-border-strong)]"
            // 36×3 per §02.6. No spacing token lands on 3; the numeric
            // inline idiom the footer and step card use carries it.
            style={{ height: 3 }}
          />
        )}
        {/* ② The close control sits first in DOM order so Radix's open
            auto-focus lands on it: "Focus moves to the dialog on open"
            arrives at the one control every sheet shares. 44×44 target. */}
        <SheetPrimitive.Close className="tlsr-focus absolute top-2 right-2 flex size-11 items-center justify-center rounded-[var(--ls-radius-0)] text-[var(--tls-fg-muted)] transition-colors hover:bg-surface-hover hover:text-[var(--tls-fg-strong)] disabled:pointer-events-none">
          <XIcon className="size-4" aria-hidden="true" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      // pr-14 keeps the title clear of the 44px close target at top-right.
      className={cn("flex flex-col gap-1.5 p-4 pr-14", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      // ④ Action bar: pinned to the bottom edge, 1px hairline above, 24
      // padding. It never scrolls with the body (§02.6 do: "Pin the action
      // bar. On a 390px screen with a keyboard open, an unpinned action bar
      // is unreachable.").
      className={cn(
        "mt-auto flex shrink-0 flex-col gap-2 border-t border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] p-6",
        className
      )}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("font-semibold text-[var(--tls-fg-strong)]", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-[var(--tls-fg-muted)]", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
